import React from "react";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import { Accordion, AccordionItem, Select } from "brass-ui-kit";

import * as styles from "./Faq.module.scss";

type FaqItem = { question: string; answer: string };
type FaqSection = {
  title: string;
  visible: boolean;
  content: FaqItem[];
};
type ScrollRequest = {
  sectionSlug: string;
  questionSlug?: string;
};

interface FaqProps {
  data: FaqSection[];
}

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const getFaqHash = (sectionSlug: string, questionSlug?: string | null) =>
  `#${sectionSlug}${questionSlug ? `/${questionSlug}` : ""}`;

const getHashParts = () => {
  if (typeof window === "undefined") {
    return { sectionSlug: "", questionSlug: "" };
  }

  const hash = decodeURIComponent(window.location.hash.replace(/^#/, ""));
  const [sectionSlug = "", questionSlug = ""] = hash.split("/");

  return { sectionSlug, questionSlug };
};

const Faq: React.FC<FaqProps> = ({ data }) => {
  const visibleSections = React.useMemo(
    () =>
      data
        .filter((item) => item.visible)
        .map((section) => ({
          ...section,
          slug: slugify(section.title),
          content: section.content.map((item) => ({
            ...item,
            slug: slugify(item.question),
          })),
        })),
    [data]
  );
  const [tabIndex, setTabIndex] = React.useState(0);
  const [scrollRequest, setScrollRequest] =
    React.useState<ScrollRequest | null>(null);
  const shouldSyncAccordionClicks = React.useRef(false);

  const updateHash = React.useCallback(
    (sectionSlug: string, questionSlug?: string | null) => {
      if (typeof window === "undefined") {
        return;
      }

      const nextHash = getFaqHash(sectionSlug, questionSlug);

      if (window.location.hash !== nextHash) {
        window.history.pushState(null, "", nextHash);
      }
    },
    []
  );

  const syncFromHash = React.useCallback(() => {
    const { sectionSlug, questionSlug } = getHashParts();

    if (!sectionSlug) {
      return;
    }

    const nextTabIndex = visibleSections.findIndex(
      (section) => section.slug === sectionSlug
    );

    if (nextTabIndex === -1) {
      return;
    }

    const nextSection = visibleSections[nextTabIndex];
    const nextQuestion = nextSection.content.find(
      (item) => item.slug === questionSlug
    );

    setTabIndex(nextTabIndex);

    if (nextQuestion) {
      setScrollRequest({
        sectionSlug: nextSection.slug,
        questionSlug: nextQuestion.slug,
      });
      return;
    }

    setScrollRequest({ sectionSlug: nextSection.slug });
  }, [visibleSections]);

  React.useEffect(() => {
    if (typeof window === "undefined" || !scrollRequest) {
      return;
    }

    const sectionIndex = visibleSections.findIndex(
      (section) => section.slug === scrollRequest.sectionSlug
    );

    if (sectionIndex === -1 || sectionIndex !== tabIndex) {
      return;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      const targetId = scrollRequest.questionSlug
        ? `${scrollRequest.sectionSlug}/${scrollRequest.questionSlug}`
        : scrollRequest.sectionSlug;
      const target = document.getElementById(targetId);
      const accordionItem = target?.querySelector(
        "[data-testid='accordion-item']"
      );
      const accordionTrigger = target?.querySelector<HTMLElement>(
        "[data-testid='accordion-trigger']"
      );

      if (
        scrollRequest.questionSlug &&
        target &&
        accordionTrigger &&
        !accordionItem?.classList.contains(styles.Faq_tabPanel_item_active)
      ) {
        accordionTrigger.click();
      }

      window.requestAnimationFrame(() => {
        target?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [scrollRequest, tabIndex, visibleSections]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    syncFromHash();

    const enableAccordionClickSync = window.setTimeout(() => {
      shouldSyncAccordionClicks.current = true;
    }, 0);

    window.addEventListener("hashchange", syncFromHash);
    window.addEventListener("popstate", syncFromHash);

    return () => {
      window.clearTimeout(enableAccordionClickSync);
      window.removeEventListener("hashchange", syncFromHash);
      window.removeEventListener("popstate", syncFromHash);
    };
  }, [syncFromHash]);

  const handleSectionSelect = (index: number) => {
    const section = visibleSections[index];

    if (!section) {
      return;
    }

    setTabIndex(index);
    updateHash(section.slug);
  };

  const handleAccordionChange = (sectionSlug: string, activeItem: string) => {
    if (!shouldSyncAccordionClicks.current) {
      return;
    }

    if (!activeItem) {
      updateHash(sectionSlug);
      return;
    }

    const accordionItem = document.querySelector(
      `div[data-id="${activeItem}"]`
    );
    const faqItem = accordionItem?.closest<HTMLElement>("[data-faq-path]");
    const faqPath = faqItem?.dataset.faqPath;

    if (!faqPath) {
      return;
    }

    const [, questionSlug] = faqPath.split("/");
    updateHash(sectionSlug, questionSlug);
  };

  return (
    <section className={styles.Faq}>
      <div className={styles.Faq_container}>
        <div className={styles.Faq_header}>
          <h1 className={styles.Faq_header_title}>
            Frequently Asked Questions
          </h1>
          <p>We answered questions so you don’t have to ask them.</p>
        </div>

        <Tabs
          selectedIndex={tabIndex}
          className={styles.Faq_list}
          onSelect={handleSectionSelect}
          selectedTabClassName={styles.Faq_tabList_item_selected}
          selectedTabPanelClassName={styles.Faq_tabPanel_selected}
        >
          <TabList className={styles.Faq_tabList}>
            {visibleSections.map(({ title }, index: number) => {
              return (
                <Tab
                  key={index}
                  className={styles.Faq_tabList_item}
                  disabledClassName={styles.Faq_tabList_item_disabled}
                >
                  <h3 className={styles.Faq_tabList_item_title}>{title}</h3>
                </Tab>
              );
            })}
          </TabList>

          <div
            style={{
              width: "100%",
            }}
            className={styles.Faq_button}
          >
            <Select
              onChange={(item) => handleSectionSelect(item.index)}
              options={visibleSections.map(({ title }, index: number) => ({
                index,
                title,
              }))}
              labelField="title"
              valueField="index"
              value={tabIndex}
            />
          </div>

          {visibleSections.map(({ content, slug: sectionSlug }, index) => {
            return (
              <TabPanel
                key={sectionSlug}
                className={`${styles.Faq_tabPanel} ${styles.Faq_tabPanel_anchor}`}
                id={sectionSlug}
              >
                <Accordion
                  onClick={(activeItem) =>
                    handleAccordionChange(sectionSlug, activeItem)
                  }
                >
                  {content.map(
                    ({ question, answer, slug }: any, itemIndex: number) => {
                      const faqPath = `${sectionSlug}/${slug}`;

                      return (
                        <div
                          className={styles.Faq_tabPanel_anchor}
                          data-faq-path={faqPath}
                          id={faqPath}
                          key={itemIndex}
                        >
                          <AccordionItem
                            header={question}
                            body={answer}
                            className={styles.Faq_tabPanel_item}
                            activeClassName={styles.Faq_tabPanel_item_active}
                          />
                        </div>
                      );
                    }
                  )}
                </Accordion>
              </TabPanel>
            );
          })}
        </Tabs>
      </div>
    </section>
  );
};

export default Faq;
